// Inventory service - server-first architecture
// All inventory data is stored in JSON files on the server
// Server handles caching and metadata generation

// Migration logic removed - server is source of truth

// Import API URL from centralized config (handles dev/prod automatically)
import { API_URL } from '../config/api';

// Server API base URL
const API_BASE = `${API_URL}/api`;

// Get metadata for a collection by folder name (server-side)
const getCollectionMetadata = async (folderName) => {
  try {
    const response = await fetch(`${API_BASE}/metadata/${encodeURIComponent(folderName)}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch metadata');
    }
    const data = await response.json();
    return data.metadata;
  } catch (error) {
    console.error('❌ Error fetching metadata from server:', error);
    return null;
  }
};

// Save metadata for a collection (server-side)
const saveCollectionMetadata = async (folderName, metadata) => {
  try {
    const response = await fetch(`${API_BASE}/metadata/${encodeURIComponent(folderName)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(metadata)
    });

    if (!response.ok) {
      throw new Error('Failed to save metadata');
    }

    const data = await response.json();
    console.log('Metadata saved to server:', data);
    return data.metadata;
  } catch (error) {
    console.error('❌ Error saving metadata to server:', error);
    return null;
  }
};

// Get all metadata from server
const getAllMetadata = async () => {
  try {
    const response = await fetch(`${API_BASE}/metadata`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch all metadata');
    }
    const data = await response.json();
    return data.metadata;
  } catch (error) {
    console.error('❌ Error fetching all metadata from server:', error);
    return {};
  }
};

// Export metadata functions for external use if needed
export const getMetadata = getCollectionMetadata;
export const saveMetadata = saveCollectionMetadata;

// No client-side caching - server handles all caching

// Load collections from server metadata
const loadCollectionsFromServer = async () => {
  try {
    const serverMetadata = await getAllMetadata();

    if (serverMetadata && Object.keys(serverMetadata).length > 0) {
      console.log(`Loaded ${Object.keys(serverMetadata).length} collections from server`);

      // Convert metadata object to array of collections
      const collections = Object.values(serverMetadata).map(col => ({
        ...col,
        items: col.items || []
      }));

      return collections;
    }

    return null;
  } catch (error) {
    console.error('Error loading collections from server:', error);
    return null;
  }
};

// ASYNC: Get all collections from server (server handles caching)
export const getAllCollectionsAsync = async () => {
  console.log('🔄 Loading collections from server...');

  try {
    // Load from server (server has its own caching)
    const serverCollections = await loadCollectionsFromServer();

    if (serverCollections && serverCollections.length > 0) {
      // Log the first collection's first item for debugging
      if (serverCollections[0] && serverCollections[0].items && serverCollections[0].items[0]) {
        const firstItem = serverCollections[0].items[0];
        console.log(`📊 Sample item from ${serverCollections[0].title}: available=${firstItem.available}`);
      }

      console.log(`✅ Loaded ${serverCollections.length} collections from server`);
      return serverCollections;
    }

    // If server returns no collections, return empty array
    console.warn('⚠️ Server returned no collections');
    return [];
  } catch (error) {
    console.error('❌ Error loading collections from server:', error);
    return [];
  }
};

// ASYNC: Get grouped collections for carousel (server handles caching)
export const getGroupedCollectionsAsync = async () => {
  try {
    // Load from server (server has its own caching)
    const serverCollections = await loadCollectionsFromServer();

    if (serverCollections && serverCollections.length > 0) {
      console.log(`✅ Loaded ${serverCollections.length} grouped collections from server`);
      return [serverCollections]; // Return as grouped array for carousel
    }

    // If server returns no collections, return empty array
    console.warn('⚠️ Server returned no collections');
    return [];
  } catch (error) {
    console.error('❌ Error loading grouped collections from server:', error);
    return [];
  }
};

// ASYNC: Get a single collection by ID (server handles caching)
export const getCollectionByIdAsync = async (id) => {
  // Load all collections from server (server caches this)
  const collections = await getAllCollectionsAsync();
  const collection = collections.find(c => c.id === id);

  if (collection) {
    console.log(`✅ Found collection ${id} from server`);
  } else {
    console.warn(`⚠️ Collection ${id} not found`);
  }

  return collection;
};

// DEPRECATED: Synchronous functions - use async versions instead
export const getAllCollections = () => {
  console.warn('⚠️ getAllCollections() is deprecated. Use getAllCollectionsAsync() instead.');
  return [];
};

export const getGroupedCollections = () => {
  console.warn('⚠️ getGroupedCollections() is deprecated. Use getGroupedCollectionsAsync() instead.');
  return [];
};

export const getCollectionById = (id) => {
  console.warn('⚠️ getCollectionById() is deprecated. Use getCollectionByIdAsync() instead.');
  return null;
};

// Update a collection (saves to server only)
export const updateCollection = async (updatedCollection) => {
  try {
    // Save the updated metadata INCLUDING items to server
    const metadata = {
      id: updatedCollection.id,
      title: updatedCollection.title,
      folder: updatedCollection.folder,
      price: updatedCollection.price,
      available: updatedCollection.available,
      medium: updatedCollection.medium,
      size: updatedCollection.size,
      year: updatedCollection.year,
      status: updatedCollection.status,
      items: updatedCollection.items || []
    };

    console.log(`💾 Saving collection ${updatedCollection.title} to server...`);
    const result = await saveCollectionMetadata(updatedCollection.folder, metadata);

    if (result) {
      console.log(`✅ Successfully saved ${updatedCollection.title} to server`);
      return true;
    } else {
      console.error(`❌ Failed to save ${updatedCollection.title} to server`);
      return false;
    }
  } catch (error) {
    console.error('❌ Error updating collection:', error);
    return false;
  }
};

// Update an item within a collection
export const updateItem = async (collectionId, updatedItem) => {
  const collection = await getCollectionByIdAsync(collectionId);
  if (!collection || !collection.items) return false;

  const itemIndex = collection.items.findIndex(item => item.id === updatedItem.id);
  if (itemIndex === -1) return false;

  collection.items[itemIndex] = updatedItem;
  return updateCollection(collection);
};

// Force update of inventory data structure (calls server to regenerate metadata)
export const updateInventoryStructure = async () => {
  try {
    console.log('🔄 Updating inventory structure via server...');

    // Call server to regenerate all metadata from current folder structure
    const success = await regenerateAllItems();

    if (success) {
      console.log('✅ Inventory structure updated successfully');
      return { success: true, newCollections: [] };
    } else {
      return { success: false, error: 'Failed to regenerate metadata on server' };
    }
  } catch (error) {
    console.error('❌ Error updating inventory structure:', error);
    return { success: false, error: error.message };
  }
};

// Regenerate all items for all collections (calls server endpoint)
export const regenerateAllItems = async () => {
  try {
    console.log('🔄 Calling server to regenerate all collection metadata...');

    const response = await fetch(`${API_BASE}/collections/regenerate-all`, {
      method: 'POST',
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Failed to regenerate collections');
    }

    const data = await response.json();
    console.log(`✅ Server regenerated ${data.results.success} collections`);

    return true;
  } catch (error) {
    console.error('❌ Error regenerating items:', error);
    return false;
  }
};

// Set all items as not available (async - updates server metadata)
export const setAllItemsNotAvailable = async () => {
  try {
    console.log('🔄 Starting setAllItemsNotAvailable...');

    // Fetch all metadata from server
    const response = await fetch(`${API_BASE}/metadata`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error('Failed to fetch metadata from server');
    }

    const data = await response.json();
    const metadata = data.metadata || {};

    console.log(`📦 Found ${Object.keys(metadata).length} collections to update`);

    // Update each collection's metadata on the server
    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (const collectionData of Object.values(metadata)) {
      try {
        console.log(`🔧 Processing ${collectionData.folder}...`);

        // Update all items to not available
        const updatedItems = (collectionData.items || []).map(item => ({
          ...item,
          available: false
        }));

        console.log(`   - Updating ${updatedItems.length} items`);

        // Update collection metadata
        const updatedMetadata = {
          ...collectionData,
          available: false,
          items: updatedItems,
          lastUpdated: new Date().toISOString()
        };

        // Save to server
        const saveResponse = await fetch(`${API_BASE}/metadata/${encodeURIComponent(collectionData.folder)}`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updatedMetadata)
        });

        if (saveResponse.ok) {
          successCount++;
          console.log(`✅ Successfully updated ${collectionData.folder}`);
        } else {
          const errorText = await saveResponse.text();
          failCount++;
          errors.push(`${collectionData.folder}: ${saveResponse.status} - ${errorText}`);
          console.error(`❌ Failed to update ${collectionData.folder}: ${saveResponse.status}`);
        }
      } catch (error) {
        failCount++;
        errors.push(`${collectionData.folder}: ${error.message}`);
        console.error(`❌ Error updating ${collectionData.folder}:`, error);
      }
    }

    console.log(`\n📊 Results: ${successCount} succeeded, ${failCount} failed`);
    if (errors.length > 0) {
      console.error('Errors:', errors);
    }

    console.log('✅ setAllItemsNotAvailable completed');
    return successCount > 0;
  } catch (error) {
    console.error('❌ Error in setAllItemsNotAvailable:', error);
    return false;
  }
};

// Delete a single item from a collection
export const deleteItem = async (collectionName, itemId) => {
  try {
    const response = await fetch(
      `${API_BASE}/items/${encodeURIComponent(collectionName)}/${encodeURIComponent(itemId)}`,
      {
        method: 'DELETE',
        credentials: 'include'
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || errorData.error || 'Failed to delete item');
    }

    const result = await response.json();
    console.log(`✅ Item deleted: ${itemId}`);
    return result;
  } catch (error) {
    console.error('❌ Error deleting item:', error);
    throw error;
  }
};

// Bulk delete items from a collection
export const bulkDeleteItems = async (collectionName, itemIds) => {
  try {
    const response = await fetch(
      `${API_BASE}/items/${encodeURIComponent(collectionName)}/bulk-delete`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ itemIds })
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || errorData.error || 'Failed to delete items');
    }

    const result = await response.json();
    console.log(`✅ Bulk deleted ${result.deletedItems?.length || 0} items`);
    return result;
  } catch (error) {
    console.error('❌ Error bulk deleting items:', error);
    throw error;
  }
};

// Export a helper to check if an item is available
export const isItemAvailable = (item) => {
  return item && item.available === true;
};

// Create a named object before exporting as default
const inventoryService = {
  getAllCollections,
  getAllCollectionsAsync,
  getGroupedCollections,
  getGroupedCollectionsAsync,
  getCollectionById,
  getCollectionByIdAsync,
  updateCollection,
  updateItem,
  deleteItem,
  bulkDeleteItems,
  updateInventoryStructure,
  regenerateAllItems,
  setAllItemsNotAvailable,
  isItemAvailable
};

export default inventoryService;